import React, { useState } from 'react';
import { Card, CardContent } from './card';
import { Badge } from './badge';
import { Award, BookOpen, Users, Tv, Lightbulb, Heart, ChevronLeft, ChevronRight, Play } from 'lucide-react';

export const FounderSection = () => {
  const [currentVideo, setCurrentVideo] = useState(0);
  
  const youtubeShorts = [
    { id: 'FpQStMNl7AU', title: 'Hair Extensions Tips' },
    { id: 'u-c20GybTJs', title: 'Salon Business Insights' },
    { id: 'EGElWlgTEKI', title: 'Hair Transformation' },
    { id: 'S2nVagNU55A', title: 'Franchise Opportunity' },
    { id: '54sZF0KdqUQ', title: 'Training Academy' },
    { id: 'B2Dgz41FNTI', title: 'Hair Business Tips' },
    { id: 'Kf_iZ1XpVrQ', title: 'Salon Success' },
    { id: '2ImBb38QJxc', title: 'Hair Extensions Guide' },
    { id: 'foOXS7yZ5Ro', title: 'Industry Insights' },
    { id: 'xF7WLHn6c_g', title: 'Entrepreneur Journey' },
    { id: 'J82IOU-s6iE', title: 'Beauty Business' },
  ];
  
  const nextVideo = () => {
    setCurrentVideo((prev) => (prev + 1) % youtubeShorts.length);
  };
  
  const prevVideo = () => {
    setCurrentVideo((prev) => (prev - 1 + youtubeShorts.length) % youtubeShorts.length);
  };

  const achievements = [
    {
      icon: Award,
      title: "26+ Awards Winner",
      detail: "Starting with 2003 Black European Businesswoman of the Year, followed by 26 awards in Hair & Beauty, Technology, Customer Services, Creative Images and Entrepreneurial.",
      color: "text-yellow-600"
    },
    {
      icon: Users,
      title: "Global Educator", 
      detail: "Trained 3,900+ students globally in advanced hair extension and hair loss treatment techniques.",
      color: "text-blue-600"
    },
    {
      icon: Lightbulb,
      title: "Industry Innovator",
      detail: "Developed 18+ pioneering hair extension techniques and revolutionary non-surgical hair loss solutions.",
      color: "text-purple-600"
    },
    {
      icon: BookOpen,
      title: "Published Author",
      detail: "Written 24+ books & beauty guides, sharing expertise with professionals worldwide.",
      color: "text-green-600"
    },
    {
      icon: Tv,
      title: "Media Personality",
      detail: "Featured in TV & print media, worked with pop icon Katy Perry, co-producer of Ladies Talk daytime TV with Angie Lamar.",
      color: "text-red-600"
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="section-title text-5xl md:text-6xl font-bold mb-6 text-dark">
            Meet the Founder
          </h2>
          <p className="text-2xl text-accent-primary font-semibold mb-4">
            Lady Diane Shawe – The Visionary Behind the Brand
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          
          {/* Founder Story */}
          <div className="space-y-6">
            <div className="relative">
              <Heart className="absolute -top-2 -left-2 w-8 h-8 text-accent-primary/20" />
              <p className="text-lg text-subtle leading-relaxed pl-6">
                <strong className="text-dark">Lady Diane Shawe</strong> is a multi-award-winning entrepreneur, educator, and author with 
                <strong className="text-accent-primary"> over 25 years of experience</strong> in the hair and beauty industry.
              </p>
            </div>
            
            <div className="bg-accent-primary/5 border-l-4 border-accent-primary p-6 rounded-r-lg">
              <p className="text-lg text-dark leading-relaxed font-medium italic">
                "She founded Need a Hair Makeover after overcoming a deeply personal battle with hair loss due to illness in 1996. 
                With no dignified help available, she turned adversity into innovation—training herself, developing groundbreaking techniques, 
                and ultimately launching specialist salons focused on hair extensions and non-surgical hair loss solutions."
              </p>
            </div>
            
            <p className="text-lg text-subtle leading-relaxed">
              Diane's journey has inspired thousands. She's served clients across the UK and Europe—including 
              <strong className="text-accent-primary"> pop icon Katy Perry</strong>—and has pioneered over 18 hair extension techniques. 
              In <strong className="text-dark">2016, she launched the Need a Hair Makeover Franchise</strong>, giving other professionals 
              a proven model to replicate her success.
            </p>
            
            <div className="bg-white border-2 border-accent-primary/20 p-6 rounded-2xl shadow-lg">
              <h3 className="text-xl font-bold text-accent-primary mb-3">Her Mission</h3>
              <p className="text-dark font-medium">
                "To help sufferers find instant, high-quality solutions and empower entrepreneurs to build their own specialist salons through supported franchising."
              </p>
            </div>
          </div>

          {/* Founder Video - Uploaded Video */}
          <div className="relative">
            <div className="aspect-[9/16] max-w-sm mx-auto rounded-2xl overflow-hidden shadow-2xl border-4 border-accent-primary/20 mb-4">
              <video
                src="https://customer-assets.emergentagent.com/job_franchise-phoenix/artifacts/dma08z68_Investing%20in%20a%20Franchise-%20More%20Than%20Just%20a%20Chair%20and%20a%20Logo-video-export-2026-01-22T10-58-12.566Z.mp4"
                title="Meet Lady Diane Shawe - The Inspirational Founder"
                className="w-full h-full object-cover"
                controls
                preload="metadata"
              >
                Your browser does not support the video tag.
              </video>
            </div>
            <p className="text-center text-sm text-subtle">
              Meet Lady Diane Shawe - The inspirational founder sharing her vision
            </p>
          </div>
        </div>

        {/* Achievements Grid */}
        <div className="mb-12">
          <h3 className="text-3xl font-bold text-center mb-12 text-dark">Unparalleled Achievements & Expertise</h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {achievements.map((achievement, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-accent-primary/30">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className={`p-3 rounded-full bg-gray-100 group-hover:bg-accent-primary/10 transition-colors duration-300`}>
                      <achievement.icon className={`w-6 h-6 ${achievement.color} group-hover:text-accent-primary transition-colors duration-300`} />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-dark mb-2 group-hover:text-accent-primary transition-colors duration-300">
                        {achievement.title}
                      </h4>
                      <p className="text-sm text-subtle leading-relaxed">
                        {achievement.detail}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Key Stats */}
        <div className="bg-white border-2 border-accent-primary text-accent-primary rounded-3xl p-12 text-center shadow-lg">
          <h3 className="text-3xl font-bold mb-8 text-accent-primary">The Numbers Speak for Themselves</h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="group">
              <div className="text-4xl md:text-5xl font-bold mb-2 group-hover:scale-110 transition-transform duration-300 text-accent-primary">25+</div>
              <div className="text-accent-primary/80">Years Experience</div>
            </div>
            <div className="group">
              <div className="text-4xl md:text-5xl font-bold mb-2 group-hover:scale-110 transition-transform duration-300 text-accent-primary">26</div>
              <div className="text-accent-primary/80">Industry Awards</div>
            </div>
            <div className="group">
              <div className="text-4xl md:text-5xl font-bold mb-2 group-hover:scale-110 transition-transform duration-300 text-accent-primary">3,900+</div>
              <div className="text-accent-primary/80">Students Trained</div>
            </div>
            <div className="group">
              <div className="text-4xl md:text-5xl font-bold mb-2 group-hover:scale-110 transition-transform duration-300 text-accent-primary">18+</div>
              <div className="text-accent-primary/80">Techniques Developed</div>
            </div>
          </div>
        </div>

        {/* YouTube Shorts Carousel */}
        <div className="mt-16 mb-16">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-dark mb-2">Watch Our Latest Videos</h3>
            <p className="text-subtle">Quick insights from Diane Shawe and our team</p>
          </div>
          
          <div className="relative max-w-4xl mx-auto">
            {/* Carousel Container */}
            <div className="flex items-center justify-center gap-4">
              {/* Previous Button */}
              <button
                onClick={prevVideo}
                className="p-3 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors z-10"
                aria-label="Previous video"
              >
                <ChevronLeft className="w-6 h-6 text-dark" />
              </button>
              
              {/* Video Display - Show 3 videos at once on desktop */}
              <div className="flex gap-4 overflow-hidden">
                {[0, 1, 2].map((offset) => {
                  const videoIndex = (currentVideo + offset) % youtubeShorts.length;
                  const video = youtubeShorts[videoIndex];
                  const isCenter = offset === 1;
                  
                  return (
                    <div
                      key={video.id}
                      className={`flex-shrink-0 transition-all duration-300 ${
                        isCenter ? 'scale-105 z-10' : 'scale-95 opacity-70 hidden md:block'
                      }`}
                    >
                      <div className="w-[280px] aspect-[9/16] rounded-2xl overflow-hidden shadow-xl bg-black">
                        <iframe
                          src={`https://www.youtube.com/embed/${video.id}`}
                          title={video.title}
                          className="w-full h-full"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Next Button */}
              <button
                onClick={nextVideo}
                className="p-3 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors z-10"
                aria-label="Next video"
              >
                <ChevronRight className="w-6 h-6 text-dark" />
              </button>
            </div>
            
            {/* Dots Indicator */}
            <div className="flex justify-center gap-2 mt-6">
              {youtubeShorts.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentVideo(index)}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    index === (currentVideo + 1) % youtubeShorts.length
                      ? 'bg-accent-primary'
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                  aria-label={`Go to video ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-3xl font-bold mb-6 text-dark">
              Join a Powerful Movement Led by One of the UK's Most Passionate Beauty Entrepreneurs
            </h3>
            
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <Badge className="bg-accent-primary/10 text-accent-primary text-sm px-4 py-2">🏆 Award-Winning Leadership</Badge>
              <Badge className="bg-accent-primary/10 text-accent-primary text-sm px-4 py-2">⭐ Celebrity Clientele</Badge>
              <Badge className="bg-accent-primary/10 text-accent-primary text-sm px-4 py-2">📚 Published Expert</Badge>
              <Badge className="bg-accent-primary/10 text-accent-primary text-sm px-4 py-2">🎓 Global Educator</Badge>
              <Badge className="bg-accent-primary/10 text-accent-primary text-sm px-4 py-2">💡 Industry Pioneer</Badge>
            </div>
            
            <p className="text-xl text-accent-primary font-semibold italic">
              "Your journey starts here"
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};